import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useOutletContext, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import UserService from "../../services/UserService";
import axios from "axios";
import _ from "lodash";
import DataCompareService from "../../services/DataCompareService";
import {Container, Row, Tooltip} from "react-bootstrap";
import TitleAndInputBox from "../common/dataView/TitleAndInputBox";
import TitleAndTextArea from "../common/dataView/TitleAndTextArea";
import DepartmentInner from "./DepartmentInner";
import {toast} from "react-toastify";
import ToastAlertView from "../common/alert/ToastAlertView";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import DeleteConfirmModal from "../common/deleteComponents/DeleteConfirmModal";
import DeleteService from "../../services/DeleteService";
import useAxios from "../../customHooks/useAxios";

const companyInfoValidMapInit = {
    companyName : true,
    description : true
};

const DeleteButton = styled.button`
  width: 150px;
  border: 1px solid #ff0000;
  border-radius: 10px;
  color: #ff0000;
  padding: 7px;
  font-size: 14px;
  background-color: white;
  transition: all ease-in-out 0.2s;
  
  &:focus {
    outline: none;
  }
  
  &:hover {
    background-color: #ffe6e6;
  }
`;

const DeleteButtonDisabled = styled.button`
  width: 150px;
  border: 1px solid #c2c2c2;
  border-radius: 10px;
  color: #c2c2c2;
  padding: 7px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
  }
`;

const SaveButton = styled.button`
  width: 150px;
  border: 1px solid #fc7242;
  border-radius: 10px;
  color: #fc7242;
  padding: 7px;
  font-size: 14px;
  background-color: white;
  transition: all ease-in-out 0.2s;
  
  &:focus {
    outline: none;
  }
  
  &:hover {
    background-color: #fcf9ed;
  }
`;

const SaveButtonDisabled = styled.button`
  width: 150px;
  border: 1px solid #c2c2c2;
  border-radius: 10px;
  color: #c2c2c2;
  padding: 7px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
  }
`;

const UpgradeButton = styled.button`
  width: 150px;
  border: 1px solid #24a0ed;
  border-radius: 10px;
  color: #24a0ed;
  padding: 7px;
  font-size: 14px;
  background-color: white;
  transition: all ease-in-out 0.2s;
  
  &:focus {
    outline: none;
  }
  
  &:hover {
    background-color: #f3ffff;
  }
`;

const UpgradeButtonDisabled = styled.button`
  width: 150px;
  border: 1px solid #c2c2c2;
  border-radius: 10px;
  color: #c2c2c2;
  padding: 7px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
  }
`;

const DegradeButton = styled.button`
  width: 150px;
  border: 1px solid #e64545;
  border-radius: 10px;
  color: #e64545;
  padding: 7px;
  font-size: 14px;
  background-color: white;
  transition: all ease-in-out 0.2s;
  
  &:focus {
    outline: none;
  }
  
  &:hover {
    background-color: #e6b8b8;
  }
`;

const DegradeButtonDisabled = styled.button`
  width: 150px;
  border: 1px solid #c2c2c2;
  border-radius: 10px;
  color: #c2c2c2;
  padding: 7px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
  }
`;

const GroupCompanySelected = () => {

    /** New : axios hook */
    const { isAxiosLoading, axiosCall } = useAxios();

    const { groupId, companyId } = useParams();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const { updateGroupAndCompanyList } = useOutletContext();

    /* states */
    /* group info */
    const [targetGroupInfo, setTargetGroupInfo] = useState({});
    /* company info */
    const [targetCompanyInfo, setTargetCompanyInfo] = useState({});
    const [fixedTargetCompanyInfo, setFixedTargetCompanyInfo] = useState({});
    const [isOnlyMasterCompany, setIsOnlyMasterCompany] = useState(false);
    /* isEdited, isValid */
    const [isCompanyInfoEdited, setIsCompanyInfoEdited] = useState(false);
    const [isCompanyInfoValid, setIsCompanyInfoValid] = useState(false);
    /* valid map */
    const [companyInfoValidMap, setCompanyInfoValidMap] = useState(companyInfoValidMapInit);

    const [isCompanyDeleteConfirmOpen, setIsCompanyDeleteConfirmOpen] = useState(false);

    /* fetch group list */
    const fetchGroupList = async () => {
        try {
            const res = await axiosCall(
                "GET",
                '/group',
                [],
                {},
                null,
                {},
                "default"
            );
            if (res.data["error_code"] !== "GET_EMPTY_DATA") {
                let newGroupList = res.data.item.groupList;
                let isFound = false;
                for (let i=0 ; i<newGroupList.length ; i++) {
                    if (newGroupList[i]['groupId'] === groupId) {
                        isFound = true;
                        let newGroupInfo = {};
                        newGroupInfo['groupName'] = newGroupList[i]['groupName'];
                        newGroupInfo['description'] = newGroupList[i]['description'];
                        newGroupInfo['isMasterGroup'] = newGroupList[i]['masterGroup'];
                        /* update states */
                        setTargetGroupInfo(_.cloneDeep(newGroupInfo));
                    }
                }
                if (!isFound) {
                    navigate('/not-found');
                }
            } else {
                setTargetGroupInfo({});
            }
        } catch (e) {
            console.log(e);
        }
    };

    /* fetch company list */
    const fetchCompanyList = async () => {
        try {
            const res = await axiosCall(
                "GET",
                '/company',
                [],
                {},
                null,
                {},
                "default"
            );
            if (res.data["error_code"] !== "GET_EMPTY_DATA") {
                let newCompanyList = res.data.item.companyList;
                let isFound = false;
                for (let i=0 ; i<newCompanyList.length ; i++) {
                    if (newCompanyList[i]['companyId'] === companyId) {
                        isFound = true;
                        let newCompanyInfo = {};
                        newCompanyInfo['companyAdminCount'] = newCompanyList[i]['companyAdminCount'];
                        newCompanyInfo['companyName'] = newCompanyList[i]['companyName'];
                        newCompanyInfo['description'] = newCompanyList[i]['description'];
                        newCompanyInfo['isMasterCompany'] = newCompanyList[i]['masterCompany'];
                        if (newCompanyInfo['isMasterCompany']) {
                            setIsOnlyMasterCompany(checkIsOnlyMasterCompanyInList(newCompanyList, newCompanyList[i]));
                        } else {
                            setIsOnlyMasterCompany(false);
                        }
                        /* update states */
                        setTargetCompanyInfo(_.cloneDeep(newCompanyInfo));
                        setFixedTargetCompanyInfo(_.cloneDeep(newCompanyInfo));
                        setCompanyInfoValidMap(companyInfoValidMapInit);
                        setIsCompanyInfoValid(true);
                        setIsCompanyInfoEdited(false);
                    }
                }
                if (!isFound) {
                    navigate('/not-found');
                }
            } else {
                setTargetCompanyInfo({});
                setFixedTargetCompanyInfo({});
                setCompanyInfoValidMap(companyInfoValidMapInit);
                setIsCompanyInfoValid(true);
                setIsCompanyInfoEdited(false);
            }
        } catch (e) {
            console.log(e);
        }
    };

    /* upgrade company */
    const upgradeCompany = async () => {
        try {
            await axiosCall(
                "PUT",
                '/company/:1/upgrade',
                [companyId],
                {},
                null,
                {},
                "default"
            );
            toast.success(<ToastAlertView message={t("message.upgraded")} />);
            await updateGroupAndCompanyList();
            await fetchGroupList();
            await fetchCompanyList();
        } catch (e) {
            console.log(e);
            toast.error(<ToastAlertView message={t("message.failed")} />);
        }
    };

    /* degrade company */
    const degradeCompany = async () => {
        try {
            await axiosCall(
                "PUT",
                '/company/:1/degrade',
                [companyId],
                {},
                null,
                {},
                "default"
            );
            toast.success(<ToastAlertView message={t("message.degraded")} />);
            await updateGroupAndCompanyList();
            await fetchGroupList();
            await fetchCompanyList();
        } catch (e) {
            console.log(e);
            toast.error(<ToastAlertView message={t("message.failed")} />);
        }
    };

    const putCompanyInfo = async () => {
        try {
            let requestBody = _.cloneDeep(targetCompanyInfo);
            delete requestBody['isMasterCompany'];
            await axiosCall(
                "PUT",
                '/company/:1',
                [companyId],
                requestBody,
                null,
                {},
                "default"
            );
            toast.success(<ToastAlertView message={t("message.saved")} />);
            await updateGroupAndCompanyList();
            await fetchGroupList();
            await fetchCompanyList();
        } catch (e) {
            console.log(e);
            toast.error(<ToastAlertView message={t("message.saveFailed")} />);
        }
    };

    /* utils */
    const checkIsOnlyMasterCompanyInList = (companyList, targetCompanyInfo) => {
        let isOnly = true;
        companyList.map(companyInfo => {
            if (companyInfo['companyGroupId'] === targetCompanyInfo['companyGroupId']) {
                if (companyInfo['companyId'] !== targetCompanyInfo['companyId']) {
                    if (companyInfo['masterCompany']) {
                        isOnly = false;
                    }
                }
            }
        });
        return isOnly;
    };

    const updateCompanyInfo = (key, value) => {
        setTargetCompanyInfo(prevObj => {
            let newObj = {...prevObj};
            newObj[key] = value;
            return newObj;
        });
    };

    const updateCompanyIsValidMap = (key, value) => {
        setCompanyInfoValidMap(prevObj => {
            let newObj = {...prevObj};
            newObj[key] = value;
            return newObj;
        });
    };

    /* input handlers */
    const handleCompanyNameInput = (value) => {
        updateCompanyInfo("companyName", value);
        if (value.length < 3 || value.length > 15) {
            updateCompanyIsValidMap("companyName", false);
        } else {
            updateCompanyIsValidMap("companyName", true);
        }
    };

    const handleCompanyDescriptionInput = (value) => {
        updateCompanyInfo("description", value);
        if (value === "") {
            updateCompanyIsValidMap("description", false);
        } else {
            updateCompanyIsValidMap("description", true);
        }
    };

    /* effects */
    /* initializing */
    useEffect(() => {
        fetchGroupList();
        fetchCompanyList();
    }, [groupId, companyId]);

    /* track isValid map */
    useEffect(() => {
        setIsCompanyInfoValid(DataCompareService.checkIsAllTrue(companyInfoValidMap));
    }, [companyInfoValidMap]);

    /* track isEdited */
    useEffect(() => {
        setIsCompanyInfoEdited(!_.isEqual(targetCompanyInfo, fixedTargetCompanyInfo));
    }, [targetCompanyInfo]);

    /* renderers */
    const renderToolTip = (name) => {
        return (
            <Tooltip id="tooltip">
                {name}
            </Tooltip>
        );
    };

    return (
        <div
            style={{
                width: '100%'
            }}
        >
            {/* company information */}
            <div
                className={"contentInner"}
            >
                <Container fluid>
                    <Row>
                        <TitleAndInputBox
                            title={t("menu.groupCompany.company.detail.companyName")}
                            value={targetCompanyInfo['companyName']}
                            onChange={handleCompanyNameInput}
                            isForEdit={true}
                            type={"text"}
                            isValid={companyInfoValidMap['companyName']}
                            isEnabled={true}
                        />
                    </Row>
                    <Row
                        style={{
                            marginTop: '10px'
                        }}
                    >
                        <TitleAndTextArea
                            title={t("menu.groupCompany.company.detail.companyDescription")}
                            value={targetCompanyInfo['description']}
                            onChange={handleCompanyDescriptionInput}
                            isForEdit={true}
                            isValid={companyInfoValidMap['description']}
                            height={"120"}
                        />
                    </Row>
                    <Row
                        style={{
                            marginTop: '10px'
                        }}
                    >
                        {/* save button */}
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'row'
                            }}
                        >
                            {/* deletion */}
                            {
                                (targetCompanyInfo['isMasterCompany'] && isOnlyMasterCompany) || (targetCompanyInfo['companyAdminCount'] > 0) ?
                                    (
                                        (targetCompanyInfo['companyAdminCount'] > 0) ?
                                            <OverlayTrigger
                                                placement={"top"}
                                                overlay={
                                                    targetCompanyInfo['companyAdminCount'] === 1 ?
                                                        renderToolTip(t("language.message.deletion.depsList.company.adminNoteSingle", { adminCount : 1 }))
                                                        :
                                                        renderToolTip(t("language.message.deletion.depsList.company.adminNote", { adminCount : targetCompanyInfo['companyAdminCount'] }))
                                                }
                                            >
                                                <DeleteButtonDisabled>
                                                    {t("language.message.deletion.button.company")}
                                                </DeleteButtonDisabled>
                                            </OverlayTrigger>
                                            :
                                            <DeleteButtonDisabled>
                                                {t("language.message.deletion.button.company")}
                                            </DeleteButtonDisabled>
                                    )
                                    :
                                    <>
                                        <DeleteButton
                                            onClick={async () => await setIsCompanyDeleteConfirmOpen(true)}
                                        >
                                            {t("language.message.deletion.button.company")}
                                        </DeleteButton>
                                        {/* delete confirmation */}
                                        <DeleteConfirmModal
                                            isOpen={isCompanyDeleteConfirmOpen}
                                            setIsOpen={setIsCompanyDeleteConfirmOpen}
                                            isDelayed={true}
                                            confirmMessage={t("language.message.deletion.confirmation", { entityName : targetCompanyInfo['companyName'] })}
                                            entity={DeleteService.ValidEntities.COMPANY}
                                            entityName={targetCompanyInfo['companyName']}
                                            id={`company/${companyId}`}
                                            onDeleteSuccess={async () => {
                                                /* have to check */
                                                await updateGroupAndCompanyList(groupId);
                                            }}
                                            depsToShow={[]}
                                            validateMessage={t("language.message.deletion.verificationMessage", { entityName : targetCompanyInfo['companyName'] })}
                                            delayToUndo={DeleteService.getMediumDelay()}
                                            onUndoSuccess={async () => {
                                                const currLocation = location.pathname;
                                                await navigate(currLocation);
                                            }}
                                        />
                                    </>
                            }
                            {/* upgrade company */}
                            {
                                !targetGroupInfo['isMasterGroup'] ?
                                    (
                                        (!targetCompanyInfo['isMasterCompany']) ?
                                            <UpgradeButton
                                                style={{
                                                    marginLeft: 'auto'
                                                }}
                                                onClick={upgradeCompany}
                                            >
                                                {t("button.upgradeCompany")}
                                            </UpgradeButton>
                                            :
                                            <UpgradeButtonDisabled
                                                style={{
                                                    marginLeft: 'auto'
                                                }}
                                            >
                                                {t("button.upgradeCompany")}
                                            </UpgradeButtonDisabled>
                                    )
                                    :
                                    <></>
                            }
                            {/* degrade company */}
                            {
                                !targetGroupInfo['isMasterGroup'] ?
                                    (
                                        targetCompanyInfo['isMasterCompany'] && (!isOnlyMasterCompany) ?
                                            <DegradeButton
                                                style={{
                                                    marginLeft: '10px'
                                                }}
                                                onClick={degradeCompany}
                                            >
                                                {t("button.degradeCompany")}
                                            </DegradeButton>
                                            :
                                            <DegradeButtonDisabled
                                                style={{
                                                    marginLeft: '10px'
                                                }}
                                            >
                                                {t("button.degradeCompany")}
                                            </DegradeButtonDisabled>
                                    )
                                    :
                                    <></>
                            }
                            {
                                isCompanyInfoEdited && isCompanyInfoValid ?
                                    <SaveButton
                                        style={
                                            targetGroupInfo['isMasterGroup'] ?
                                                {
                                                    marginLeft: 'auto'
                                                }
                                                :
                                                {
                                                    marginLeft: '10px'
                                                }
                                        }
                                        onClick={() => {
                                            putCompanyInfo();
                                        }}
                                    >
                                        {t("button.save")}
                                    </SaveButton>
                                    :
                                    <SaveButtonDisabled
                                        style={
                                            targetGroupInfo['isMasterGroup'] ?
                                                {
                                                    marginLeft: 'auto'
                                                }
                                                :
                                                {
                                                    marginLeft: '10px'
                                                }
                                        }
                                    >
                                        {t("button.save")}
                                    </SaveButtonDisabled>
                            }
                        </div>
                    </Row>
                </Container>
            </div>

            {/* departments */}
            <div
                style={{
                    width: '100%',
                    marginTop: '10px'
                }}
            >
                <DepartmentInner />
            </div>
        </div>
    );
};

export default GroupCompanySelected;